/**
 * The client half of the request-signing scheme.
 *
 * The canonical string here must match `Devices::RequestSignature.canonical_string`
 * byte for byte. Both sides have a test pinning the exact bytes, because the
 * failure mode when they drift is a server answering `device_signature_invalid`
 * to every request, which points at the key rather than at the string.
 */
import { encodeUtf8, sign } from "./keypair"

export const DEVICE_HEADER = "X-ERegister-Device"
export const TIMESTAMP_HEADER = "X-ERegister-Timestamp"
export const NONCE_HEADER = "X-ERegister-Nonce"
export const SIGNATURE_HEADER = "X-ERegister-Signature"

export interface SignatureInput {
  method: string
  /** Path *and* query, exactly as it goes on the wire. */
  fullPath: string
  body: string
  timestamp: string
  nonce: string
}

export function canonicalString({ method, fullPath, body, timestamp, nonce }: SignatureInput & { body: string }): string {
  return [method.toUpperCase(), fullPath, body, timestamp, nonce].join("\n")
}

export async function buildCanonicalString(input: SignatureInput): Promise<string> {
  return canonicalString({ ...input, body: await sha256Hex(input.body) })
}

export async function signedHeaders(
  deviceUuid: string,
  privateKey: CryptoKey,
  input: Omit<SignatureInput, "timestamp" | "nonce">,
  clock: () => number = Date.now
): Promise<Record<string, string>> {
  // Whole seconds, because that is what the server parses. A nonce makes the
  // second-granularity irrelevant: two requests in the same second are still
  // two different requests.
  const timestamp = Math.floor(clock() / 1000).toString()
  const nonce = generateNonce()
  const message = await buildCanonicalString({ ...input, timestamp, nonce })

  return {
    [DEVICE_HEADER]: deviceUuid,
    [TIMESTAMP_HEADER]: timestamp,
    [NONCE_HEADER]: nonce,
    [SIGNATURE_HEADER]: await sign(privateKey, message)
  }
}

export function generateNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", encodeUtf8(value))
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("")
}
